import React, { useCallback, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { buttonClickHandler } from '../buttons/button-click-handler';
import { ACTION_ATTR, applyAction, extractAction, readAction } from './html-action-bridge';
import './index.css';

/**
 * Opt-in marker for HTML content.
 *
 * A TEXT payload is rendered as markdown unless it is wrapped in a single
 * `<html>…</html>` envelope, which is what distinguishes "this message is
 * HTML" from "this message merely happens to mention a tag". Leading and
 * trailing whitespace is tolerated; anything outside the envelope is not, so a
 * message that only embeds `<html>` mid-sentence stays markdown.
 */
const HTML_ENVELOPE = /^\s*<html\s*>([\s\S]*)<\/html\s*>\s*$/i;

export function isHtmlContent(text) {
  return typeof text === 'string' && HTML_ENVELOPE.test(text);
}

/** Content inside the envelope, or '' when the text is not HTML. */
export function extractHtml(text) {
  const match = typeof text === 'string' ? text.match(HTML_ENVELOPE) : null;
  return match ? match[1] : '';
}

/**
 * Tags and attributes an agent may use.
 *
 * An allow-list, not a block-list: anything not named here is dropped. Script,
 * iframe, object, form and event handlers are all therefore excluded by
 * omission, as are `javascript:` URLs (DOMPurify rejects those by default).
 */
const ALLOWED_TAGS = [
  'a', 'b', 'blockquote', 'br', 'button', 'code', 'dd', 'div', 'dl', 'dt', 'em',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
  'pre', 's', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody',
  'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
];

// The `bc-*` attributes are written by the action bridge, not by the agent: an
// `on_action(...)` handler is parsed into them and the original `onclick` is
// dropped. They are listed here so DOMPurify keeps what the bridge just set.
// (Not `data-bc-*`: `ALLOW_DATA_ATTR: false` strips every `data-*` attribute
// regardless of this list.)
const ALLOWED_ATTR = [
  'href', 'title', 'alt', 'src', 'colspan', 'rowspan', 'target', 'rel', 'style',
  'type', 'bc-action', 'bc-slot', 'bc-value', 'bc-title', 'bc-next-state',
];

/**
 * CSS properties an inline `style` may not set.
 *
 * `style` is allowed so agents can send self-contained rich content (payment
 * cards, receipts), but a message must not be able to escape its bubble. These
 * are the properties that would let it: taking the element out of flow to cover
 * the page, floating above it, or making it interactive as a fake control. Note
 * `position` is refused outright rather than restricted to `static`, since a
 * sticky/fixed value is the whole risk.
 */
const FORBIDDEN_STYLE = /^(position|z-index|top|right|bottom|left|inset(-|$)|transform|cursor|pointer-events|content|animation|transition|will-change|filter|backdrop-filter|clip|clip-path|mix-blend-mode|isolation)/i;

/** Value-level guards: URLs and expressions can smuggle behaviour into CSS. */
const FORBIDDEN_STYLE_VALUE = /(url\s*\(|expression\s*\(|javascript:|@import|behavior\s*:|-moz-binding)/i;

const SAFE_URI = /^(?:https?:|mailto:|tel:|#|\/)/i;

/**
 * Strip disallowed declarations from one inline style attribute.
 *
 * Parsed off the element's own `style` object rather than by splitting the
 * string, so the browser has already normalised whatever the agent sent.
 */
function sanitizeStyleAttribute(node) {
  const declarations = node.style;
  if (!declarations || declarations.length === 0) {
    return;
  }

  const kept = [];
  for (let index = 0; index < declarations.length; index += 1) {
    const property = declarations[index];
    const value = declarations.getPropertyValue(property);

    if (FORBIDDEN_STYLE.test(property) || FORBIDDEN_STYLE_VALUE.test(value)) {
      continue;
    }
    kept.push(`${property}:${value}`);
  }

  if (kept.length === 0) {
    node.removeAttribute('style');
  } else {
    node.setAttribute('style', kept.join(';'));
  }
}

/**
 * Post-sanitize pass over each surviving element.
 *
 * 1. Drop `data:` URLs. `img` is in DOMPurify's built-in data-URI tag set, and
 *    `ADD_DATA_URI_TAGS` only extends that set - it cannot shrink it - so
 *    `ALLOWED_URI_REGEXP` never gets consulted for `img[src]`.
 * 2. Filter inline styles down to presentational declarations only.
 * 3. Pin link targets. `target="_blank"` without `rel="noopener"` hands the
 *    opened page a reference back to this window.
 */
function hardenNode(node) {
  ['src', 'href'].forEach(attribute => {
    const value = node.getAttribute?.(attribute);
    if (value && !SAFE_URI.test(value.trim())) {
      node.removeAttribute(attribute);
    }
  });

  if (node.hasAttribute?.('style')) {
    sanitizeStyleAttribute(node);
  }

  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }

  // Written here rather than in the element phase: attributes set earlier are
  // dropped by the URI check that ALLOWED_URI_REGEXP applies to every custom
  // attribute value.
  applyAction(node);
}

/**
 * Convert an `on_action(...)` handler into data attributes.
 *
 * Runs in the element phase, which is the last point at which `onclick` is
 * still readable - DOMPurify removes it during attribute sanitisation. The
 * attribute is only ever *parsed*, never executed.
 */
function liftActions(node) {
  if (node.nodeType !== 1) {
    return;
  }
  extractAction(node);
}

export function sanitizeHtml(html) {
  DOMPurify.addHook('uponSanitizeElement', liftActions);
  DOMPurify.addHook('afterSanitizeAttributes', hardenNode);
  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP: SAFE_URI,
      ALLOW_DATA_ATTR: false,
      // Both can host script-bearing content that is not plain HTML.
      FORBID_TAGS: ['svg', 'math'],
    });
  } finally {
    DOMPurify.removeHook('uponSanitizeElement');
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}

/**
 * Render agent-supplied HTML.
 *
 * The content is sanitized before it reaches the DOM - it arrives from the
 * agent, which may be relaying user- or tool-supplied text, so it is treated as
 * untrusted regardless of who authored the workflow.
 *
 * Clicks are handled by delegation rather than by binding each element: the
 * markup is injected as a string, so there is nothing to attach a React handler
 * to. One listener on the container covers every action in the message.
 */
export default function HtmlMessage({ html, onInvokeAction, disabled }) {
  const clean = useMemo(() => sanitizeHtml(html), [html]);

  // Read inside the listener so it does not need re-binding on every render.
  const handlerRef = useRef(null);
  handlerRef.current = { onInvokeAction, disabled };

  const onClick = useCallback(event => {
    const trigger = event.target.closest(`[${ACTION_ATTR}]`);
    if (!trigger) {
      return;
    }

    event.preventDefault();

    const { onInvokeAction: invoke, disabled: isDisabled } = handlerRef.current;
    if (isDisabled || typeof invoke !== 'function') {
      return;
    }

    const action = readAction(trigger);
    if (action) {
      // Same handler the BUTTONS message type uses, so an HTML action and a
      // native button produce an identical request.
      buttonClickHandler(invoke)(action.payload, action.requestAttributes);
    }
  }, []);

  return (
    <div
      className="conv-html-message prose chat-text-bubble chat-bot-bubble"
      onClick={onClick}
      // Safe: `clean` is DOMPurify output restricted to the allow-list above.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
